"use client"

import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { SortingState } from '@tanstack/react-table'
import type { FilterValues } from '@open-mercato/ui/backend/FilterBar'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { Page, PageBody, PageHeader } from '@open-mercato/ui/backend/Page'
import { DynamicTable } from '@open-mercato/ui/backend/DynamicTable'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { buildCrudQuery, deleteCrud } from '@open-mercato/ui/backend/utils/crud'
import { Button } from '@open-mercato/ui/primitives/button'
import {
  buildLegsColumns,
  buildLegsFilters,
  type LegListItem,
  type LegsListResponse,
} from '../../lib/legTable'
import { LegFormDialog } from '../../lib/legFormDialog'
import { AssignmentDialog } from '../../lib/assignmentDialog'

const DEFAULT_PAGE_SIZE = 25

function buildLegsListParams(page: number, search: string, filterValues: FilterValues, sorting: SortingState) {
  const sort = sorting[0]
  return {
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    q: search.trim() || undefined,
    status: typeof filterValues.status === 'string' ? filterValues.status : undefined,
    vehicleType: typeof filterValues.vehicleType === 'string' ? filterValues.vehicleType : undefined,
    sortField: sort?.id,
    sortDir: sort?.desc ? 'desc' : 'asc',
  }
}

async function fetchLegsList(params: ReturnType<typeof buildLegsListParams>): Promise<LegsListResponse> {
  return readApiResultOrThrow<LegsListResponse>(`/api/road/legs?${buildCrudQuery(params)}`, undefined, {
    errorMessage: 'Failed to fetch legs.',
  })
}

export default function LegsPage() {
  const t = useT()
  const scopeVersion = useOrganizationScopeVersion()
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation({ contextId: 'legs-page' })

  const [page, setPage] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [sorting, setSorting] = React.useState<SortingState>([{ id: 'scheduledPickupAt', desc: false }])
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})

  const [createOpen, setCreateOpen] = React.useState(false)
  const [editTarget, setEditTarget] = React.useState<LegListItem | null>(null)
  const [editOpen, setEditOpen] = React.useState(false)
  const [assignTarget, setAssignTarget] = React.useState<LegListItem | null>(null)
  const [assignOpen, setAssignOpen] = React.useState(false)

  const params = React.useMemo(
    () => buildLegsListParams(page, search, filterValues, sorting),
    [page, search, filterValues, sorting],
  )

  const legsQuery = useQuery({
    queryKey: ['legs', 'list', scopeVersion, params],
    queryFn: () => fetchLegsList(params),
  })

  const refresh = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['legs'] })
    await queryClient.invalidateQueries({ queryKey: ['dispatch'] })
  }, [queryClient])

  const handleDelete = React.useCallback(async (row: LegListItem) => {
    const approved = await confirm({
      title: t('dispatch.confirm.deleteTitle', 'Delete leg?'),
      text: t('dispatch.confirm.deleteMessage', 'This will archive the leg and keep audit history intact.'),
      confirmText: t('dispatch.actions.delete', 'Delete'),
      cancelText: t('dispatch.actions.cancelEdit', 'Cancel'),
      variant: 'destructive',
    })
    if (!approved) return
    try {
      await runMutation({
        context: { kind: 'road.leg', record: { id: row.id } },
        mutationPayload: { id: row.id },
        operation: async () => {
          await deleteCrud(`road/legs/${row.id}`, {
            errorMessage: t('dispatch.messages.deleteError', 'Failed to delete leg.'),
          })
        },
      })
      flash(t('dispatch.messages.deleteSuccess', 'Leg deleted.'), 'success')
      await refresh()
    } catch {
      flash(t('dispatch.messages.deleteError', 'Failed to delete leg.'), 'error')
    }
  }, [confirm, refresh, runMutation, t])

  const columns = React.useMemo(() => buildLegsColumns(t), [t])
  const filters = React.useMemo(() => buildLegsFilters(t), [t])

  if (legsQuery.isLoading && !legsQuery.data) {
    return (
      <Page>
        <PageHeader
          title={t('dispatch.pages.legs.pageTitle', 'Legs')}
          description={t('dispatch.pages.legs.description', 'Manage transport legs for road dispatch.')}
        />
        <PageBody>
          <LoadingMessage label={t('dispatch.messages.loading', 'Loading dispatch board…')} />
        </PageBody>
      </Page>
    )
  }

  if (legsQuery.isError) {
    return (
      <Page>
        <PageHeader
          title={t('dispatch.pages.legs.pageTitle', 'Legs')}
          description={t('dispatch.pages.legs.description', 'Manage transport legs for road dispatch.')}
        />
        <PageBody>
          <ErrorMessage
            label={t('dispatch.messages.loadErrorTitle', 'Could not load dispatch board')}
            description={t('dispatch.messages.loadError', 'Try again in a moment or refresh the page.')}
          />
        </PageBody>
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        title={t('dispatch.pages.legs.pageTitle', 'Legs')}
        description={t('dispatch.pages.legs.description', 'Manage transport legs for road dispatch.')}
        actions={(
          <Button type="button" onClick={() => setCreateOpen(true)}>
            {t('dispatch.actions.addLeg', 'Add leg')}
          </Button>
        )}
      />
      <PageBody>
        <DynamicTable
          title={t('dispatch.pages.legs.tableTitle', 'Legs')}
          columns={columns}
          data={legsQuery.data?.items ?? []}
          sortable={true}
          sorting={sorting}
          onSortingChange={(next) => { setSorting(next); setPage(1) }}
          pagination={legsQuery.data ? {
            page: legsQuery.data.page,
            pageSize: legsQuery.data.pageSize,
            total: legsQuery.data.total,
            totalPages: legsQuery.data.totalPages,
            onPageChange: setPage,
          } : undefined}
          searchValue={search}
          onSearchChange={(value) => { setSearch(value); setPage(1) }}
          searchPlaceholder={t('dispatch.pages.board.searchPlaceholder', 'Search by cargo ID or notes')}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={(next) => { setFilterValues(next); setPage(1) }}
          onFiltersClear={() => { setFilterValues({}); setPage(1) }}
          rowActions={(row) => (
            <RowActions
              items={[
                ...(row.status === 'CREATED' ? [{
                  id: 'assign',
                  label: t('dispatch.actions.assign', 'Assign'),
                  onSelect: () => {
                    setAssignTarget(row)
                    setAssignOpen(true)
                  },
                }] : []),
                {
                  id: 'edit',
                  label: t('dispatch.actions.edit', 'Edit'),
                  onSelect: () => {
                    setEditTarget(row)
                    setEditOpen(true)
                  },
                },
                {
                  id: 'delete',
                  label: t('dispatch.actions.delete', 'Delete'),
                  onSelect: () => { void handleDelete(row) },
                  destructive: true,
                },
              ]}
            />
          )}
        />
      </PageBody>

      {ConfirmDialogElement}

      <LegFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={() => { void refresh() }}
      />

      <LegFormDialog
        open={editOpen}
        onOpenChange={(open) => {
          if (!open) setEditTarget(null)
          setEditOpen(open)
        }}
        leg={editTarget}
        onSuccess={() => { void refresh() }}
      />

      <AssignmentDialog
        open={assignOpen}
        onOpenChange={(open) => {
          if (!open) setAssignTarget(null)
          setAssignOpen(open)
        }}
        leg={assignTarget}
        onSuccess={() => { void refresh() }}
      />
    </Page>
  )
}
